import React, { useEffect, useRef, useState } from 'react';

/**
 * Premium count-up that animates 0 → `to` once when scrolled into view.
 *
 * - Uses IntersectionObserver (no library) and requestAnimationFrame.
 * - Cubic ease-out for that "settling" luxury feel.
 * - Respects `prefers-reduced-motion`: snaps to final value instantly.
 * - Renders the suffix (e.g. "+") separately so number formatting is clean.
 *
 * Props:
 *   to        — final integer value
 *   duration  — ms (default 2000)
 *   suffix    — string rendered after the number (default '+')
 *   format    — boolean, locale-format the number with thousands separator
 *   className — passthrough for typography
 */
const CountUp = ({
  to = 0,
  duration = 2000,
  suffix = '+',
  format = true,
  className = '',
  testid = 'count-up',
}) => {
  const [value, setValue] = useState(0);
  const ref = useRef(null);
  const startedRef = useRef(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    const reduced =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

    const run = () => {
      if (startedRef.current) return;
      startedRef.current = true;

      if (reduced) {
        setValue(to);
        return;
      }
      const start = performance.now();
      const tick = (now) => {
        const elapsed = now - start;
        const t = Math.min(1, elapsed / duration);
        // cubic ease-out
        const eased = 1 - Math.pow(1 - t, 3);
        setValue(Math.round(eased * to));
        if (t < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    };

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            run();
            io.disconnect();
          }
        });
      },
      { threshold: 0.4 },
    );
    io.observe(node);
    return () => io.disconnect();
  }, [to, duration]);

  const display = format ? value.toLocaleString('en-US') : String(value);

  return (
    <span
      ref={ref}
      data-testid={testid}
      className={`tabular-nums tracking-tight transition-opacity duration-500 ${className}`}
    >
      {display}
      {suffix}
    </span>
  );
};

export default CountUp;
